import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { provideAnimations } from '@angular/platform-browser/animations';
import { translate } from '@jsverse/transloco';
import { ReplaySubject } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { DEFAULT_CONTENT_CHANNEL } from 'app/modules/upload/components/upload-create/collection-configuration.service';
import { ConfigurationRouteService } from 'app/providers/semantria-api/configuration-route.service';
import { ConfigurationService } from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { LanguageTemplateService } from 'app/providers/semantria-api/language-template.service';
import {
  OriginTypes,
  SemantriaAccountService,
} from 'app/providers/semantria-api/semantria-account.service';

import { Collection, CollectionService } from 'app/providers/storage-api/collection.service';
import { DownloadTasksService, TaskType } from 'app/providers/tasks-api/download.service';
import { AlertService } from 'app/services/alert.service';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetDataSource } from '../../../../models';
import { generalNameValidator } from '../../../../utils/customValidators';

// eslint-disable-next-line
export interface SaveDocumentsToCollectionOptions {
  projectId: string;
  widgetDataSource: WidgetDataSource;
  query: string;
  destinationType: 'NEW' | 'EXISTING';
  source: 'WIDGET' | 'REPORT';
}

export interface SaveDocumentsToCollectionDialogResult {
  createNewCollection: boolean;
  target: string;
}

enum CopyState {
  START = 'START',
  COPYING = 'COPYING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

@EnableClassTelemetry()
@Component({
  selector: 'app-save-to-collection-dialog',
  templateUrl: './save-to-collection-dialog.component.html',
  styleUrls: ['./save-to-collection-dialog.component.scss'],
  imports: [AppSharedModule],
  providers: [provideAnimations()],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveDocumentsToCollectionDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  options = input.required<SaveDocumentsToCollectionOptions>();
  modalClosed = output<SaveDocumentsToCollectionDialogResult | null>();

  sort = viewChild(MatSort);

  dataSource = signal(new MatTableDataSource<Collection>());
  displayedColumns = ['displayName', 'analysisCount', 'documentCount', 'analysisType'];

  state = signal(CopyState.START);
  isLoading = signal(false);
  targetDisplayName = signal('');
  destinationType = signal('');
  createNewCollection = signal(false);
  target = signal('');
  collections: Collection[] = [];

  collectionNameStatus = signal<ErrorObject>({ state: EnumErrorStates.SUCCESS, text: '' });

  hasError = computed(
    () => !this.target() || this.collectionNameStatus().state !== EnumErrorStates.SUCCESS
  );

  configNamesById = new Map<string, string>();

  newCollection = signal<Collection | undefined>(undefined);
  originTypes = OriginTypes;

  private unsub$ = new ReplaySubject<void>(1);

  private readonly collectionService = inject(CollectionService);
  private readonly groupService = inject(GroupService);
  private readonly configService = inject(ConfigurationService);
  private readonly configRouteService = inject(ConfigurationRouteService);
  private readonly accountService = inject(SemantriaAccountService);
  private readonly languageTemplateService = inject(LanguageTemplateService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly taskService = inject(DownloadTasksService);
  private readonly alertService = inject(AlertService);

  async ngOnInit() {
    this.destinationType.set(this.options().destinationType);
    this.createNewCollection.set(this.destinationType() === 'NEW');
    this.target.set('');

    this.isLoading.set(true);
    try {
      this.loadConfigNames();

      const result = await this.collectionService.getCollections(this.options().projectId);
      this.collections = result.items;
      this.dataSource.set(
        new MatTableDataSource(
          this.collections.filter(
            (coll) => coll.collectionName !== this.options().widgetDataSource.collectionName
          )
        )
      );

      if (this.destinationType() === 'EXISTING') {
        this.dataSource().sort = this.sort();
        this.dataSource().sortData = (data, sort) => {
          const dir = sort.direction === 'asc' ? 1 : -1;

          if (sort.active === 'displayName') {
            return data.sort((a, b) => a.displayName.localeCompare(b.displayName) * dir);
          } else {
            return data.sort((a, b) => (a[sort.active] - b[sort.active]) * dir);
          }
        };
        this.dataSource().sort.sort({ id: 'displayName', start: 'asc', disableClear: true });
      } else {
        this.newCollection.set(
          this.collections.find(
            (col) => col.collectionName === this.options().widgetDataSource.collectionName
          )
        );
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  private loadConfigNames() {
    this.configService
      .getConfigurations(false, this.groupService.getSelectedGroupId())
      .then((res) => res.forEach((c) => this.configNamesById.set(c.id, c.name)));
    this.configRouteService
      .getConfigurationRoutes(this.groupService.getSelectedGroupId())
      .then((res) =>
        res.forEach((c) => {
          this.configNamesById.set(`cr:${c.route_id}`, c.name);
          Object.keys(c.configuration_map).forEach((lang) =>
            this.configNamesById.set(
              `cr:${c.route_id}:${lang}`,
              `${c.name} (${lang.toUpperCase()})`
            )
          );
        })
      );
    this.languageTemplateService
      .getLanguageTemplates()
      .then((res) => res.forEach((c) => this.configNamesById.set(`lt:${c.id}`, c.name)));
    this.accountService.sub
      .takeUntil(this.unsub$)
      .subscribe((res) =>
        res.industry_packs.forEach((c) => this.configNamesById.set(`ip:${c.id}`, c.name))
      );
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  isRowDisabled(collection: Collection) {
    return (
      collection.locked ||
      collection.origin.some(
        (originName) =>
          originName === OriginTypes.RT ||
          originName === OriginTypes.ESAAS ||
          originName === OriginTypes.XI
      )
    );
  }

  toggleSelectedCollection(collection: Collection) {
    if (this.isRowDisabled(collection)) {
      return;
    }
    if (this.target() === collection.collectionName) {
      this.target.set('');
    } else {
      this.target.set(collection.collectionName);
    }
  }

  onValidateCollectionName() {
    const status = { ...this.collectionNameStatus() };
    generalNameValidator(
      this.target(),
      status,
      translate('save-to-collection-dialog.collection'),
      64,
      true,
      this.collections.map((collection) => collection.displayName),
      false
    );
    this.collectionNameStatus.set(status);
  }

  public cancel(): void {
    this.modalClosed.emit(null);
  }

  public async confirm(): Promise<void> {
    if (this.hasError()) {
      return;
    }

    this.state.set(CopyState.COPYING);
    try {
      let collection = this.collections.find((col) => col.collectionName === this.target());
      if (this.createNewCollection()) {
        this.targetDisplayName.set(this.target());
        collection = await this.collectionService.createCollection(this.options().projectId, {
          name: this.target(),
          contentChannel: this.options().widgetDataSource.contentChannel ?? DEFAULT_CONTENT_CHANNEL,
        });
      }
      const collectionName = collection.collectionName;
      this.targetDisplayName.set(collection.displayName);
      const payload: any = {
        _type: TaskType.COPY_COLLECTION,
        projectId: this.options().projectId,
        collectionName: this.options().widgetDataSource.collectionName,
        targetCollectionName: collectionName,
        queryString: this.options().query,
        timezone: this.timezoneService.timezone,
      };

      if (this.options().widgetDataSource.type === 'analysis') {
        payload.analysisName = this.options().widgetDataSource.analysisId;
      } else {
        payload.analysisGroupId = this.options().widgetDataSource.groupId;
      }

      await this.taskService.createTask(payload);

      this.alertService.showToast(translate('save-to-collection-dialog.copiing'));

      this.state.set(CopyState.COMPLETE);
      this.close();
    } catch (e) {
      this.alertService.showErrorToast(translate('save-to-collection-dialog.copy-failed'), e);
      this.state.set(CopyState.ERROR);
    }
  }

  public close() {
    this.modalClosed.emit({
      createNewCollection: this.createNewCollection(),
      target: this.target(),
    });
  }
}
